import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { catchError, throwError } from 'rxjs'
import { ToastService } from '../services/toast.service'
import { inject } from '@angular/core'
import { HttpErrorHandlerService } from '../services/http-error-handler.service'
import { RETRY_SUCCESS_HEADER } from './retry.interceptor'
import { Router } from '@angular/router'
import { AuthService } from '../services/auth.service'

// Check if error is "too many requests" (could be 429 or 500 with specific message)
function isTooManyRequestsError(error: HttpErrorResponse): boolean {
  if (error.status === 429) return true

  // Check for 500 errors that are actually "too many requests" from Cognito
  if (error.status === 500 && error.error?.message) {
    const errorMsg = error.error.message.toLowerCase()
    return (
      errorMsg.includes('too many requests') ||
      errorMsg.includes('rate exceeded')
    )
  }

  return false
}

// Check if error is "Invalid user token" which should trigger login modal instead of toast
// Only applies when the request had an Authorization header (i.e., was an authenticated request)
function isInvalidTokenError(error: HttpErrorResponse, req: any): boolean {
  return (
    error.status === 401 &&
    error.error?.message === 'Invalid user token' &&
    req.headers.has('Authorization')
  ) // Only for requests that were sent with auth token
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService)
  const errorHandler = inject(HttpErrorHandlerService)
  const router = inject(Router)
  const authService = inject(AuthService)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if this request was successfully retried (has our header)
      const wasRetried = error.headers?.get(RETRY_SUCCESS_HEADER) === 'true'

      // Check if it's a "too many requests" error
      const isTooManyRequests = isTooManyRequestsError(error)

      // Check if it's an "Invalid user token" error on an authenticated request
      const isInvalidToken = isInvalidTokenError(error, req)

      // Get detailed diagnostic info about the error
      const errorInfo = errorHandler.getDiagnosticErrorMessage(
        error,
        `${req.method} ${req.url}`,
      )

      // Log detailed information for developers
      console.error(errorInfo.technicalMessage)

      // Check for misleading CORS errors and log appropriate warnings
      if (error.message && error.message.includes('CORS')) {
        errorHandler.diagnoseMisleadingCorsError(error)

        // Replace cryptic error message in console with more helpful one
        console.error(
          '❌ The browser reported a CORS error, but this is often misleading. ' +
            'Check the logs above for possible real causes. The original error was:',
          error.message,
        )
      }

      // Handle invalid token errors on authenticated requests by showing login modal instead of toast
      if (isInvalidToken) {
        console.log(
          'Invalid user token detected on authenticated request, showing login modal',
        )
        authService.logout()

        // Store the current URL for redirection after login, but only if not on home page
        const currentUrl = window.location.pathname + window.location.search
        if (currentUrl !== '/' && currentUrl !== '') {
          console.log('Storing URL for redirect after token error:', currentUrl)
          localStorage.setItem('redirectUrl', currentUrl)
        } else {
          localStorage.removeItem('redirectUrl')
        }

        // Navigate to login modal
        router.navigate([{ outlets: { modal: 'auth' } }], {
          skipLocationChange: true,
        })

        // Re-throw the error so it can be handled by the calling component
        return throwError(() => error)
      }

      // Check if this is a silent request that should not show error messages
      const isSilentRequest = req.headers.has('X-Silent-Request')

      // Don't show toast for successfully retried requests, rate limit errors that will be retried, or invalid token errors
      const shouldSuppressToast =
        wasRetried ||
        isSilentRequest || // Suppress toast for requests marked as silent
        (isTooManyRequests && req.method !== 'GET') || // Suppressing toast for non-GET requests that are rate limited
        isInvalidToken || // Suppress toast for invalid token errors since we show login modal instead
        (error.error?.message &&
          (error.error.message.toLowerCase().includes('user is disabled') ||
            error.error.message
              .toLowerCase()
              .includes('userdisabledexception'))) // Suppress toast for disabled account errors

      // Show toast for non-GET requests to avoid spamming the user during normal navigation
      // But only if we're not suppressing it due to retry success or special error handling
      if (req.method !== 'GET' && !shouldSuppressToast) {
        let message = errorInfo.userMessage

        // For rate limit errors, provide a more helpful message
        if (isTooManyRequests) {
          message = 'The server is busy. Please try again in a moment.'
        }

        // For connection errors (status 0), show AWS outage message instead of generic error
        if (error.status === 0) {
          // AWS outage toast disabled
          // toastService.presentToast({
          //   message:
          //     'Our site is currently experiencing issues due to an ongoing Amazon Web Service outage over the past two days. Thank you for understanding!',
          //   color: 'warning',
          //   duration: 10000,
          //   position: 'top',
          //   cssClass: 'aws-outage-toast',
          //   buttons: [
          //     {
          //       text: 'See Status',
          //       handler: () => {
          //         window.open(
          //           'https://downdetector.com/status/aws-amazon-web-services/',
          //           '_blank',
          //         )
          //       },
          //     },
          //   ],
          // })
        } else {
          toastService.presentToast({
            message: message,
            color: 'danger',
          })
        }
      }

      // Re-throw the error so it can be handled by the calling component
      return throwError(() => error)
    }),
  )
}

function getErrorMessage(err: HttpErrorResponse, req: any): string {
  if (!navigator.onLine) {
    return 'You appear to be offline. Please check your internet connection.'
  }

  if (err.error?.message) {
    return err.error.message
  }

  switch (err.status) {
    case 0:
      return 'Unable to connect to the server. Please try again later.'
    case 400:
      return 'The request was invalid. Please check your input and try again.'
    case 401:
      return 'You need to be logged in to perform this action.'
    case 403:
      return 'You do not have permission to perform this action.'
    case 404:
      return 'The requested resource was not found.'
    case 409:
      return 'There was a conflict with the current state of the resource.'
    case 500:
    case 502:
    case 503:
    case 504:
      return 'The server encountered an error. Please try again later.'
    default:
      return `There was an error. Please try again (${err.status || 'unknown'}).`
  }
}
